/** @format */
// import './pairing-model.styles.scss';
import * as d3 from 'd3';
import React, { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import json from './sample_data.json';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import useWindowDimensions from '../../utils/useWindowDimensions.hook';

import { useDispatch, useSelector } from 'react-redux';

import {
  getPairingModelAvailableModelsStart,
  setSelectedPairingModel,
  getSelectedPairingModelDataStart,
  clearSelectedPairingModelData,
} from '../../redux/pairing-model/pairing-model.actions';

import {
  selectPairingModelAvailableModels,
  selectPairingModelSelectedModel,
  selectPairingModelSelectedModelData,
} from '../../redux/pairing-model/pairing-model.selectors';
import { motion } from 'framer-motion';

const dimensions = {
  width: 1200,
  height: 880,
  contextHeight: 240,
  focusHeight: 640,
  margin: {
    top: 20,
    right: 50,
    bottom: 20,
    left: 20,
  },
};
const rectHeight = 50;
const hourDistance = [5, 24];
const dayDistance = [1, 3];
const dutyDistance = [1, 6];
const minDistanceHour = 5;
const minDistanceDay = 1;
const hourMarks = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24,
].map((d, i) => {
  return {
    value: i,
    label: d,
  };
});

// {
//     "duty_city": "LAS",
//     "departure_station": "LAS",
//     "arrival_station": "LAX",
//     "utc_local_time_variation_departure": -700.0,
//     "date": "2023-10-01",
//     "duty_idx": 90,
//     "dep_unix": "2023-10-01 17:40:00",
//     "arr_unix": "2023-10-01 18:50:00"
//   },

const rectDepartureStation = (d) => {
  return d.departure_station;
};

const rectArrivalStation = (d) => {
  return d.arrival_station;
};

const rectDepartureTime = (d) => {
  return d.dep_unix;
};

const rectArrivalTime = (d) => {
  return d.arr_unix;
};

const rectDutyDate = (d) => {
  return d.date;
};

function wrap(text, width) {
  text.each(function () {
    var text = d3.select(this),
      words = text.text().split(/\s+/).reverse(),
      word,
      line = [],
      lineNumber = 0,
      lineHeight = 1.1, // ems
      x = text.attr('x'),
      y = text.attr('y'),
      dy = 0, //parseFloat(text.attr("dy")),
      tspan = text
        .text(null)
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', dy + 'em');
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = text
          .append('tspan')
          .attr('x', x)
          .attr('y', y)
          .attr('dy', ++lineNumber * lineHeight + dy + 'em')
          .text(word);
      }
    }
  });
}

// console.log(data);

const DutiesVisualization = () => {
  const dispatch = useDispatch();
  const svgRef = useRef(null);

  // On Mount get available models
  useEffect(() => {
    dispatch(getPairingModelAvailableModelsStart());
  }, []);

  let availableModels = useSelector(selectPairingModelAvailableModels);
  let selectedModelData = useSelector(selectPairingModelSelectedModelData);
  let selectedModel = useSelector(selectPairingModelSelectedModel);
  let windowDimensions = useWindowDimensions();

  let [data, setData] = React.useState([]);
  let [start_date, setStartDate] = React.useState(dayjs().format('YYYY-MM-DD'));
  let [end_date, setEndDate] = React.useState(
    dayjs().add(1, 'day').format('YYYY-MM-DD')
  );
  let [dutyDates, setDutyDates] = React.useState([start_date, end_date]);
  let [marks, setMarks] = React.useState(
    dutyDates.map((d, i) => {
      return {
        value: i,
        label: dayjs(d).format('MM/DD'),
      };
    })
  );
  let [dutyCities, setDutyCities] = React.useState(['DAL']);

  let [filteredData, setFilteredData] = React.useState([]);
  let [dutyCityFilter, setDutyCityFilter] = React.useState('DAL');
  let [filterStartDate, setFilterStartDate] = React.useState(start_date);
  let [filterEndDate, setFilterEndDate] = React.useState(end_date);

  // !Selected Model changed
  useEffect(() => {
    if (selectedModel) {
      console.log('selectedModel changed');
      dispatch(getSelectedPairingModelDataStart());
    } else {
      dispatch(clearSelectedPairingModelData());
    }
  }, [selectedModel]);

  // !Selected Model Data changed
  useEffect(() => {
    if (selectedModelData) {
      let tempData = Object.keys(selectedModelData)
        .map((key) => json[key])
        .sort((a, b) => {
          let aDate = dayjs(a.date);
          let bDate = dayjs(b.date);
          if (aDate.isBefore(bDate)) {
            return -1;
          }
          if (aDate.isAfter(bDate)) {
            return 1;
          }
          return 0;
        });

      setData(tempData);

      setStartDate(dayjs(tempData[0].date).format('YYYY-MM-DD'));
      setEndDate(
        dayjs(tempData[tempData.length - 1].date).format('YYYY-MM-DD')
      );

      setFilterStartDate(dayjs(tempData[0].date).format('YYYY-MM-DD'));
      setFilterEndDate(dayjs(tempData[0].date).format('YYYY-MM-DD'));
      let tempDutyDates = [...new Set(tempData.map((d) => d.date))].sort(
        (a, b) => {
          let aDate = dayjs(a);
          let bDate = dayjs(b);
          if (aDate.isBefore(bDate)) {
            return -1;
          }
          if (aDate.isAfter(bDate)) {
            return 1;
          }
          return 0;
        }
      );

      setDutyDates(tempDutyDates);

      setMarks(
        tempDutyDates.map((d, i) => {
          return {
            value: i,
            label: dayjs(d).format('MM/DD'),
          };
        })
      );
      let tempDuyCities = [...new Set(tempData.map((d) => d.duty_city))].sort(
        (a, b) => {
          return a.localeCompare(b);
        }
      );
      setDutyCities(tempDuyCities);

      let filteredData = tempData.filter((d) => d.duty_city === dutyCityFilter);
      let startDate = dayjs(filterStartDate).startOf('day');
      let endDate = dayjs(filterEndDate).endOf('day');
      filteredData = filteredData.filter((d) => {
        let date = dayjs(d.date);
        return (
          (date.isAfter(startDate) && date.isBefore(endDate)) ||
          date.isSame(startDate) ||
          date.isSame(endDate)
        );
      });

      // let dutyIdxMap = {};
      // dutyIndicies.forEach((d, i) => {
      //   dutyIdxMap[d] = i;
      // });

      // setDutyIndiciesMap(dutyIdxMap);
    } else {
      setData([]);
      setStartDate(dayjs().format('YYYY-MM-DD'));
      setEndDate(dayjs().add(1, 'day').format('YYYY-MM-DD'));

      setFilterStartDate(dayjs().format('YYYY-MM-DD'));
      setFilterEndDate(dayjs().format('YYYY-MM-DD'));
      let tempDutyDates = [
        dayjs().format('YYYY-MM-DD'),
        dayjs().add(1, 'day').format('YYYY-MM-DD'),
      ].sort((a, b) => {
        let aDate = dayjs(a);
        let bDate = dayjs(b);
        if (aDate.isBefore(bDate)) {
          return -1;
        }
        if (aDate.isAfter(bDate)) {
          return 1;
        }
        return 0;
      });

      setDutyDates(tempDutyDates);

      setMarks(
        tempDutyDates.map((d, i) => {
          return {
            value: i,
            label: dayjs(d).format('MM/DD'),
          };
        })
      );
      setDutyCities(['DAL']);
      setDutyCityFilter('DAL');
    }
  }, [selectedModelData]);

  //! Draw chart
  useEffect(() => {
    if (filteredData.length > 0) {
      //Make chart

      filteredData = filteredData.filter((d) => d.duty_city === dutyCityFilter);
      // filteredData = filteredData.filter((d) => d.date === filterDate);
      let startDate = dayjs(filterStartDate).startOf('day');
      let endDate = dayjs(filterEndDate).endOf('day');

      let dateDiff = {
        days: endDate.diff(startDate, 'day'),
        hours: endDate.diff(startDate, 'hour'),
        minutes: endDate.diff(startDate, 'minute'),
      };

      filteredData = filteredData.filter((d) => {
        let date = dayjs(d.date);
        return (
          (date.isAfter(startDate) && date.isBefore(endDate)) ||
          date.isSame(startDate) ||
          date.isSame(endDate)
        );
      });

      let dutyIndicies = [...new Set(filteredData.map((d) => d.duty_idx))];
      let dutyIdxMap = {};
      dutyIndicies.forEach((d, i) => {
        dutyIdxMap[d] = i;
      });
      let yDomain = [...Object.values(dutyIdxMap).sort((a, b) => a - b)];

      let svg = d3.select(svgRef.current);
      let YAxis = d3
        .scalePoint()
        .domain(yDomain)
        .range([
          dimensions.height -
            dimensions.margin.bottom -
            rectHeight -
            dimensions.contextHeight,
          dimensions.margin.top + rectHeight,
        ]);
      let XAxis = d3
        .scaleTime()
        .domain([
          new Date(dayjs(filterStartDate).startOf('day').valueOf()),
          new Date(dayjs(filterEndDate).endOf('day').valueOf()),
        ])
        .range([
          dimensions.margin.left,
          dimensions.width - dimensions.margin.right,
        ]);
      const gy = svg
        .append('g')
        .attr('transform', `translate(${dimensions.margin.left},0)`)
        .call(d3.axisLeft(YAxis));
      // d3.timeMinute.every(15)

      let ticker = d3.timeHour.every(2);
      if (dateDiff.days < 4) {
        ticker = d3.timeHour.every(6);
      } else if (dateDiff.days > 3 && dateDiff.days < 7) {
        ticker = d3.timeHour.every(12);
      } else if (dateDiff.days >= 7) {
        ticker = d3.timeDay.every(1);
      }

      const gx = svg
        .append('g')
        .attr(
          'transform',
          `translate(0,${
            dimensions.height -
            dimensions.margin.bottom -
            dimensions.contextHeight
          })`
        )
        .call(
          d3
            .axisBottom(XAxis)
            .ticks(ticker)
            .tickFormat(d3.timeFormat('%H:%M %d'))
        );
      let rects = svg
        .selectAll('.duty')
        .data(filteredData)
        .enter()
        .filter((d) => {
          return isNaN(YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2)
            ? false
            : true;
        })
        .append('g')
        .classed('duty', true)
        .append('rect')
        .attr('x', (d) => {
          let depTime = rectDepartureTime(d);
          return XAxis(new Date(dayjs(depTime).valueOf()));
        })
        .attr('y', (d) => {
          return YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2;
        })
        .attr('width', (d) => {
          let depTime = rectDepartureTime(d);
          let arrTime = rectArrivalTime(d);
          return Math.abs(
            XAxis(new Date(dayjs(arrTime).valueOf())) -
              XAxis(new Date(dayjs(depTime).valueOf()))
          );
        })
        .attr('height', rectHeight)
        .attr('stroke', 'black')
        .attr('fill', 'red');
      rects.on('mouseover', function (event, d) {
        d3.select(this).attr('fill', 'blue');
        alert(JSON.stringify(d, null, 2));
      });
      // Add text to rects
      let textLabels = svg
        .selectAll('.duty')
        .append('text')
        .text((d) => {
          return `${d.departure_station} - ${d.arrival_station}`;
        })
        .attr('x', (d) => {
          let depTime = rectDepartureTime(d);
          let arrTime = rectArrivalTime(d);
          return (
            Math.abs(
              XAxis(new Date(dayjs(depTime).valueOf())) +
                XAxis(new Date(dayjs(arrTime).valueOf()))
            ) /
              2 -
            20
          );
        })
        .attr('y', (d) => {
          return YAxis(dutyIdxMap[d.duty_idx]);
        })
        .attr('font-family', 'sans-serif')
        .call(wrap, 60);
      return () => {
        svg.selectAll('.duty').remove();
        gx.remove();
        gy.remove();
      };
    }
  }, [filteredData]);

  // ! Draw context with brush
  useEffect(() => {
    data = data.filter((d) => d.duty_city === dutyCityFilter);
    if (selectedModelData) {
      let dutyIndicies = [...new Set(data.map((d) => d.duty_idx))];
      let dutyIdxMap = {};
      dutyIndicies.forEach((d, i) => {
        dutyIdxMap[d] = i;
      });
      let yDomain = [...Object.values(dutyIdxMap).sort((a, b) => a - b)];
      let rectHeight = 5;
      let svg = d3.select(svgRef.current);

      let YAxis = d3
        .scalePoint()
        .domain(yDomain)
        .range([
          dimensions.height - dimensions.margin.bottom - rectHeight,
          dimensions.focusHeight + dimensions.margin.top + rectHeight,
        ]);

      let XAxis = d3
        .scaleTime()
        .domain([
          new Date(
            dayjs(start_date)
              .startOf('day')

              .valueOf()
          ),
          new Date(
            dayjs(end_date)
              .endOf('day')

              .valueOf()
          ),
        ])
        .range([
          dimensions.margin.left,
          dimensions.width - dimensions.margin.right,
        ]);

      let rects = svg
        .selectAll('rect')
        .data(data)
        .enter()
        .filter((d) => {
          return isNaN(YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2)
            ? false
            : true;
        })
        .append('g')
        .classed('context-duty', true)
        .append('rect')
        .attr('x', (d) => {
          let depTime = rectDepartureTime(d);

          return XAxis(new Date(dayjs(depTime).valueOf()));
        })

        .attr('y', (d) => {
          return YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2;
        })
        .attr('width', (d) => {
          let depTime = rectDepartureTime(d);
          let arrTime = rectArrivalTime(d);

          return Math.abs(
            XAxis(new Date(dayjs(arrTime).valueOf())) -
              XAxis(new Date(dayjs(depTime).valueOf()))
          );
        })
        .attr('height', rectHeight)
        .attr('fill', 'red');

      const gx = svg
        .append('g')
        .attr(
          'transform',
          `translate(0,${dimensions.height - dimensions.margin.bottom})`
        )
        .call(
          d3
            .axisBottom(XAxis)
            .ticks(d3.timeDay.every(1))
            .tickFormat(d3.timeFormat('%m/%d'))
        );

      const defaultSelection = [
        [
          XAxis(new Date(dayjs(start_date).startOf('day').valueOf())),
          dimensions.focusHeight + dimensions.margin.top + 180,
        ],
        [
          XAxis(new Date(dayjs(start_date).endOf('day').valueOf())),
          dimensions.height - dimensions.margin.bottom,
        ],
      ];

      const brush = d3
        .brush()
        .extent([
          [
            dimensions.margin.left,
            dimensions.focusHeight + dimensions.margin.top,
          ],
          [
            dimensions.width - dimensions.margin.right,
            dimensions.height - dimensions.margin.bottom,
          ],
        ])
        .on('brush', brushed);

      const brushG = svg
        .append('g')
        .classed('brush', true)
        .call(brush)
        .call(brush.move, defaultSelection);

      function brushed({ selection }) {
        if (selection) {
          setFilterStartDate(
            dayjs(XAxis.invert(selection[0][0])).format('YYYY-MM-DD')
          );
          setFilterEndDate(
            dayjs(XAxis.invert(selection[1][0])).format('YYYY-MM-DD')
          );

          let selectedRects = rects.filter((d) => {
            let depTime = rectDepartureTime(d);
            let arrTime = rectArrivalTime(d);
            let [minX, minY] = selection[0];
            let [maxX, maxY] = selection[1];

            let x1 = XAxis(new Date(dayjs(depTime).valueOf()));
            let x2 = XAxis(new Date(dayjs(arrTime).valueOf()));

            let y1 = YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2;
            let y2 = YAxis(dutyIdxMap[d.duty_idx]) + rectHeight / 2;

            return x1 >= minX && x2 <= maxX && y1 >= minY && y2 <= maxY;
          });
          selectedRects.attr('fill', 'blue');

          setFilteredData(selectedRects.data());

          let unselectedRects = rects.filter((d) => {
            let depTime = rectDepartureTime(d);
            let arrTime = rectArrivalTime(d);
            let [minX, minY] = selection[0];
            let [maxX, maxY] = selection[1];

            let x1 = XAxis(new Date(dayjs(depTime).valueOf()));
            let x2 = XAxis(new Date(dayjs(arrTime).valueOf()));

            let y1 = YAxis(dutyIdxMap[d.duty_idx]) - rectHeight / 2;
            let y2 = YAxis(dutyIdxMap[d.duty_idx]) + rectHeight / 2;

            return (
              (x1 < minX || x2 > maxX || y1 < minY || y2 > maxY) &&
              d3.select(this).attr('fill') !== 'blue'
            );
          });
          unselectedRects.attr('fill', 'red');
        }
      }

      function brushended({ selection }) {
        if (!selection) {
          brushG.call(brush.move, defaultSelection);
        } else {
          setFilterStartDate(
            dayjs(XAxis.invert(selection[0][0])).format('YYYY-MM-DD')
          );
          setFilterEndDate(
            dayjs(XAxis.invert(selection[1][0])).format('YYYY-MM-DD')
          );
        }
      }

      return () => {
        svg.selectAll('.context-duty').remove();
        gx.remove();
        // gy.remove();
        svg.selectAll('.brush').remove();
      };
    }
  }, [data, dutyCityFilter, selectedModel, selectedModelData]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     console.log('changing filterEndDate');
  //     setFilterEndDate('2023-10-02');
  //   }, 5000);
  //   setTimeout(() => {
  //     console.log('changing filterStartDate');
  //     setFilterStartDate('2023-10-02');
  //   }, 10000);
  // }, []);

  return (
    <>
      <Box
        sx={{
          minWidth: 120,
          backgroundColor: '#e0e0e0',
          m: '2%',
          p: '2%',
          borderRadius: '6px',
        }}
      >
        <FormControl required fullWidth>
          <InputLabel
            variant="standard"
            htmlFor="select-available-paring-models-controlled-native"
            size="large"
          >
            Select a model
          </InputLabel>
          <NativeSelect
            defaultValue={''}
            onChange={(event) => {
              // setSelectedModel(event.target.value);
              let model = availableModels.find(
                (m) => m.name === event.target.value
              );
              dispatch(setSelectedPairingModel(model));
            }}
            inputProps={{
              name: 'Select a model',
              id: 'select-available-paring-models-controlled-native',
            }}
          >
            <option aria-label="None" value="" />
            {availableModels.map((model) => {
              return (
                <option key={model.name + model.expiry} value={model.name}>
                  {model.name}
                </option>
              );
            })}
          </NativeSelect>
        </FormControl>
      </Box>
      <Box
        sx={{
          minWidth: 120,
          backgroundColor: '#e0e0e0',
          m: '2%',
          p: '2%',
          borderRadius: '6px',
        }}
      >
        <FormControl required fullWidth>
          <InputLabel
            variant="standard"
            htmlFor="select-dutyCity-paring-models-controlled-native"
            size="large"
          >
            Select a Duty City
          </InputLabel>
          <NativeSelect
            defaultValue={'DAL'}
            onChange={(event) => {
              // setSelectedModel(event.target.value);
              setDutyCityFilter(event.target.value);
            }}
            inputProps={{
              name: 'Select Duty City',
              id: 'select-dutyCity-paring-models-controlled-native',
            }}
          >
            {dutyCities.map((city) => {
              return (
                <option key={city} value={city}>
                  {city}
                </option>
              );
            })}
          </NativeSelect>
        </FormControl>
      </Box>
      <Box
        sx={{
          backgroundColor: '#e0e0e0',
          paddingRight: '10%',
          borderRadius: '6px',
          paddingLeft: '2%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <div style={{ width: '100%' }}>
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
          />
        </div>
      </Box>
    </>
  );
};

export default DutiesVisualization;
